import { useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  DataTable,
  MoneyDisplay,
  type Column,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import {
  quoteStatus,
  useWithdrawQuoteMutation,
  type Quote,
  type QuoteStatus,
} from '@entities/rfq';

const STATUS_TONE: Record<QuoteStatus, 'neutral' | 'success' | 'info'> = {
  ACTIVE: 'info',
  WITHDRAWN: 'neutral',
  ACCEPTED: 'success',
};

export function QuotesTable({ data }: { data: Quote[] }) {
  const { t } = useTranslation();
  const withdraw = useWithdrawQuoteMutation();
  const [error, setError] = useState<string | null>(null);

  const onWithdraw = (q: Quote) => {
    if (!window.confirm(t('quotes.withdrawConfirm'))) return;
    setError(null);
    withdraw.mutate(q.id, {
      onError: (err) => setError(err instanceof ApiError ? err.message : t('quotes.loadFail', { message: '' })),
    });
  };

  const columns: Column<Quote>[] = [
    {
      key: 'createdAt',
      header: t('quotes.columns.created'),
      cell: (q) => new Date(q.createdAt).toLocaleDateString(),
      width: '120px',
    },
    {
      key: 'request',
      header: t('quotes.columns.buyRequest'),
      cell: (q) => q.buyRequestTitle ?? q.buyRequestId.slice(0, 8),
    },
    {
      key: 'price',
      header: t('quotes.columns.price'),
      cell: (q) => <MoneyDisplay money={q.priceTotal} />,
    },
    {
      key: 'lead',
      header: t('quotes.columns.leadTime'),
      cell: (q) => `${q.leadTimeDays} d`,
    },
    {
      key: 'status',
      header: t('common.status'),
      cell: (q) => {
        const s = quoteStatus(q);
        return <Badge tone={STATUS_TONE[s]}>{t(`quotes.status.${s}`)}</Badge>;
      },
    },
    {
      key: 'actions',
      header: '',
      width: '140px',
      cell: (q) => {
        const s = quoteStatus(q);
        if (s !== 'ACTIVE') return null;
        return (
          <Button
            type="button"
            size="sm"
            variant="outline"
            loading={withdraw.isPending && withdraw.variables === q.id}
            onClick={() => onWithdraw(q)}
          >
            {t('quotes.withdraw')}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      {error ? (
        <Alert type="error" showIcon message={error} />
      ) : null}
      <DataTable data={data} columns={columns} rowKey={(q) => q.id} />
    </div>
  );
}
